import { faCircleExclamation } from "@fortawesome/pro-regular-svg-icons/faCircleExclamation";
import { faArrowUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons/faArrowUpRightFromSquare";
import { faLightbulb } from "@fortawesome/pro-solid-svg-icons/faLightbulb";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons/faTriangleExclamation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Flex, Text } from "@mantine/core";
import Link from "next/link";
import { type ReactNode, useCallback } from "react";
import { twMerge } from "tailwind-merge";

import type { QuestionWarning } from "@/types/screener-builder/questions";
import { AlertSeverity, type SingleOrArray } from "@/types/utils";

interface QuestionWarningAlertProps {
  children?: ReactNode;
  warning?: SingleOrArray<QuestionWarning>;
}

const sharedClassNames = {
  icon: "mr-3",
  message: "text-xs whitespace-pre-wrap",
  root: "px-3 py-3.5",
};

const SEVERITY_STYLE_MAP = {
  [AlertSeverity.Error]: {
    alertClassName: "bg-uw-red-1",
    icon: faCircleExclamation,
    iconClassName: "text-uw-red",
  },
  [AlertSeverity.Info]: {
    alertClassName: "bg-uw-purple-0",
    icon: faLightbulb,
    iconClassName: "",
  },
  [AlertSeverity.Warning]: {
    alertClassName: "bg-uw-yellow-2",
    icon: faTriangleExclamation,
    iconClassName: "text-[#ED9600]",
  },
};

export const QuestionWarningAlert = ({
  children,
  warning,
}: QuestionWarningAlertProps) => {
  const getIcon = useCallback((severity: AlertSeverity) => {
    const { icon, iconClassName } = SEVERITY_STYLE_MAP[severity];

    return <FontAwesomeIcon className={iconClassName} icon={icon} />;
  }, []);
  const getClassNames = useCallback(
    (severity: AlertSeverity) => ({
      ...sharedClassNames,
      root: twMerge(
        SEVERITY_STYLE_MAP[severity].alertClassName,
        sharedClassNames.root,
      ),
    }),
    [],
  );

  const renderAlert = useCallback(
    ({ helpHref, message, severity }: QuestionWarning) => (
      <Alert
        classNames={getClassNames(severity)}
        icon={getIcon(severity)}
        key={message}
      >
        {children ?? (
          <Flex direction="column">
            <Text size="sm">{message}</Text>
            {helpHref && (
              <Link
                className="font-semibold text-xs uppercase w-24"
                href={helpHref}
                rel="noopener noreferrer"
                target="_blank"
              >
                Learn More
                <FontAwesomeIcon
                  className="ml-1.5 mt-2"
                  icon={faArrowUpRightFromSquare}
                />
              </Link>
            )}
          </Flex>
        )}
      </Alert>
    ),
    [children, getClassNames, getIcon],
  );

  if (!warning) {
    return null;
  }

  return Array.isArray(warning) ? (
    <>{warning.map(renderAlert)}</>
  ) : (
    renderAlert(warning)
  );
};
